import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormDraftSubmitInput } from "@colab/shared";
import type { SubmitFormResult } from "./types";

// POST /api/portal/forms/drafts/:submissionId/submit — promote draft →
// pending and run the bucket-6 acceptance pipeline. Pessimistic: final
// submission is the moment server-side validation matters most. Replaces
// the bucket-6 useSubmitFormPartner hook.
//
// 400 responses with `code: 'validation'` carry per-field Zod issues in
// `details.issues` — extractValidationIssues() peels them off so the page
// can drive rhf.setError(name, ...). The contract is byte-identical to the
// legacy bucket-6 hook so any consumer using `extractValidationIssues` is
// preserved.
//
// onSuccess invalidates: list + detail (next page-visit refetches the
// completed form view), deals.all + portal.all (auto-accept may surface
// a new deal / lead; matches the bucket-6 invalidation broadcast).
export function useSubmitForm(slug: string, submissionId: string | null) {
  const qc = useQueryClient();
  return useMutation<SubmitFormResult, ApiClientError, FormDraftSubmitInput>({
    mutationFn: (body) => {
      if (!submissionId) {
        throw new ApiClientError(
          0,
          "no_submission_id",
          "Cannot submit: missing submission id",
        );
      }
      return apiFetch<SubmitFormResult>(
        `/portal/forms/drafts/${submissionId}/submit`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.portal.forms.list() });
      void qc.invalidateQueries({
        queryKey: queryKeys.portal.forms.detail(slug),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.deals.all });
      void qc.invalidateQueries({ queryKey: queryKeys.portal.all });
    },
  });
}

// Tiny helper for the page caller: peel per-field Zod issues out of a 400
// body so it can drive react-hook-form's setError. Worker ships the issues
// array via the standard ApiError envelope's details field
// (`{ error: { code: "validation", message, details: { issues } } }`), so
// the SPA reads them off ApiClientError.details. Re-exported from this hook
// to preserve the legacy bucket-6 public API (the old hook
// use-submit-form-partner.ts also exported it).
export type ValidationIssue = z.ZodIssue;

export function extractValidationIssues(
  err: ApiClientError,
): ReadonlyArray<ValidationIssue> | null {
  if (err.code !== "validation") return null;
  const details = err.details as unknown;
  if (!details || typeof details !== "object") return null;
  const maybe = details as { issues?: unknown };
  if (!Array.isArray(maybe.issues)) return null;
  return maybe.issues as ValidationIssue[];
}
